"use client"

import { fr } from "@codegouvfr/react-dsfr"
import Checkbox from "@codegouvfr/react-dsfr/Checkbox"
import Input from "@codegouvfr/react-dsfr/Input"
import RadioButtons from "@codegouvfr/react-dsfr/RadioButtons"
import { Box } from "@mui/material"
import type { ReactNode } from "react"
import { useMemo, useRef } from "react"

import { MATOMO_EVENTS, pushMatomoEvent, SEARCH_ENGINES } from "@/utils/matomo-utils"

import type { ISearchPageParams } from "../_utils/search.params.utils"
import { SearchChipOptionRow, SearchFilterChip } from "./SearchFilterChip"

interface FacetCounts {
  type?: Record<string, number>
  type_filter_label?: Record<string, number>
  contract_type?: Record<string, number>
  level?: Record<string, number>
  activity_sector?: Record<string, number>
  organization_name?: Record<string, number>
}

interface SearchFiltersProps {
  params: ISearchPageParams
  facets?: FacetCounts
  /** Compteurs des chips booléennes (counts de l'API : handi, urgent, candidature simplifiée). */
  counts?: { is_disabled_elligible?: number; urgent?: number; smart_apply?: number }
  /** Total de résultats de la recherche courante — sert à désactiver les filtres qui n'affineraient rien. */
  nbHits?: number
  onNavigate: (newParams: ISearchPageParams) => void
  /** "bar" : rangée de chips desktop ; "sections" : modale Filtres mobile (sections empilées). */
  variant?: "bar" | "sections"
  /** Variante "sections" : fermeture de la modale, déclenchée par Entrée dans le champ date. */
  onClose?: () => void
}

/** Suffixe « (N) » des libellés de chips à compteur. */
const withCount = (label: string, count?: number) => (count !== undefined ? `${label} (${count})` : label)

// Valeurs de niveau « indifférentes » (toujours incluses côté API) : pas des options de filtre.
const LEVEL_AGNOSTIC_VALUES = new Set(["", "Indifférent"])

// Ordre pédagogique Cap → Bac+5 (l'ordre alphabétique mélange les niveaux).
const levelRank = (label: string): number => {
  if (/infrabac/i.test(label)) return 1
  if (/\(bac\)/i.test(label)) return 2
  if (/bac\+2/i.test(label)) return 3
  if (/bac\+3/i.test(label)) return 4
  if (/bac\+5/i.test(label)) return 5
  return 99
}

/** Options d'un filtre : valeurs des facettes + valeurs sélectionnées (toujours démontables). */
function buildOptionValues(counts?: Record<string, number>, selected: string[] = []): string[] {
  return [...new Set([...Object.keys(counts ?? {}), ...selected])]
}

/** Libellé de chip multi-valeurs : « Apprentissage, +1 ». */
const multiLabel = (values: string[]): string => (values.length > 1 ? `${values[0]}, +${values.length - 1}` : values[0])

const formatDateFr = (isoDate: string): string => isoDate.split("-").reverse().join("/")

const isFutureDate = (isoDate?: string): boolean => {
  if (!isoDate) return false
  const today = new Date()
  const todayIso = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`
  return isoDate > todayIso
}

const TYPE_FILTER_LABEL_DISTANCE = "Formation à distance"

// Calendrier natif retiré du champ date : son bouton prend un arrêt de tabulation dont le
// comportement clavier n'est pas fiable, la date se saisit au clavier (RGAA 7.3 couvert). Sans
// effet sur Firefox (pas de pseudo-élément) ni sur mobile (le sélecteur s'ouvre au toucher).
// L'icône DSFR (::after décoratif, place réservée par padding-right) partirait sinon sans bouton.
const HIDDEN_DATE_PICKER_SX = {
  "& input[type='date']": { paddingRight: "1rem" },
  "& input[type='date']::-webkit-calendar-picker-indicator, & input[type='date']::after": { display: "none" },
}

// Désactivé avec le calendrier natif (cf. HIDDEN_DATE_PICKER_SX), à rétablir avec lui.
// Touches qui modifient la date depuis le champ. Un `change` qui ne suit aucune d'elles vient du
// calendrier natif, dont les clics et les touches (Entrée comprise) ne parviennent pas à la page.
// const DATE_EDIT_KEY = /^(\d|Backspace|Delete|ArrowUp|ArrowDown)$/

// Popper à checkboxes : neutralise les marges basses du fieldset DSFR (1rem sur le fieldset
// ET sur son dernier élément) pour retomber sur le padding vertical du panneau (8px),
// identique aux poppers mono-choix.
const CHECKBOX_POPPER_SX = {
  px: "16px",
  pt: "8px",
  "& .fr-fieldset": { mb: 0 },
  "& .fr-fieldset__element:last-child": { mb: 0 },
}

/** Les filtres remis à zéro par « Réinitialiser les filtres » (q, lieu, mode et tri conservés). */
export function clearedFilters(params: ISearchPageParams): ISearchPageParams {
  return {
    ...params,
    type_filter_label: undefined,
    contract_type: undefined,
    level: undefined,
    activity_sector: undefined,
    organization_name: undefined,
    start_date: undefined,
    urgent: undefined,
    handi: undefined,
    smart_apply: undefined,
    is_algo_company: undefined,
    page: 0,
  }
}

export function hasActiveFilters(params: ISearchPageParams): boolean {
  return Boolean(
    params.type_filter_label?.length ||
      params.contract_type?.length ||
      params.level?.length ||
      params.activity_sector?.length ||
      params.organization_name ||
      params.start_date ||
      params.urgent !== undefined ||
      params.handi !== undefined ||
      params.smart_apply !== undefined ||
      params.is_algo_company?.length
  )
}

const MOBILE_SECTION_TITLE_SX = { fontSize: "1rem", fontWeight: 700, color: fr.colors.decisions.text.default.grey.default }

/**
 * Section de la modale Filtres mobile : titre gras + contenu, séparée par un filet. Pour un groupe
 * de cases ou de radios, le titre est la légende du fieldset (pas de `title`) : un `h3` en plus
 * ferait annoncer deux fois le nom de la section.
 */
function MobileSection({ title, children }: { title?: string; children: ReactNode }) {
  return (
    <Box
      sx={{
        py: fr.spacing("4v"),
        borderBottom: `1px solid ${fr.colors.decisions.border.default.grey.default}`,
        "& .fr-fieldset": { mb: 0 },
        // react-dsfr pose `fr-text--regular` (font-weight !important) sur la légende.
        "& .fr-fieldset__legend": { ...MOBILE_SECTION_TITLE_SX, fontWeight: "700 !important", pb: fr.spacing("3v") },
      }}
    >
      {title && (
        <Box component="h3" sx={{ ...MOBILE_SECTION_TITLE_SX, margin: 0, mb: fr.spacing("3v") }}>
          {title}
        </Box>
      )}
      {children}
    </Box>
  )
}

export function SearchFilters({ params, facets, counts, nbHits, onNavigate, variant = "bar", onClose }: SearchFiltersProps) {
  // search_filter_opened : dropdown ouvert puis refermé SANS application.
  // `navigate` marque le dropdown ouvert comme « appliqué » ; la fermeture sans application émet l'événement.
  const openDropdownRef = useRef<{ filterName: string; applied: boolean } | null>(null)
  const trackDropdown = (filterName: string) => (open: boolean) => {
    if (open) {
      openDropdownRef.current = { filterName, applied: false }
      return
    }
    if (openDropdownRef.current?.filterName === filterName && !openDropdownRef.current.applied) {
      pushMatomoEvent({ event: MATOMO_EVENTS.SEARCH_FILTER_OPENED, filter_name: filterName, search_engine: SEARCH_ENGINES.BETA })
    }
    openDropdownRef.current = null
  }

  const navigate = (patch: Partial<ISearchPageParams>) => {
    if (openDropdownRef.current) openDropdownRef.current.applied = true
    onNavigate({ ...params, ...patch, page: 0 })
  }

  const contractOptions = useMemo(
    () => buildOptionValues(facets?.contract_type, params.contract_type).sort((a, b) => a.localeCompare(b, "fr")),
    [facets?.contract_type, params.contract_type]
  )
  const levelOptions = useMemo(
    () =>
      buildOptionValues(facets?.level, params.level)
        .filter((v) => !LEVEL_AGNOSTIC_VALUES.has(v))
        .sort((a, b) => levelRank(a) - levelRank(b)),
    [facets?.level, params.level]
  )

  const isFormations = params.mode === "formations"
  const selectedLevel = params.level?.[0]
  const futureStartDate = isFutureDate(params.start_date)

  // Un filtre booléen « n'affine pas » la recherche quand aucun résultat ne le satisfait
  // (l'activer viderait la liste) ou quand tous le satisfont déjà (l'activer ne changerait
  // rien) → désactivé. Jamais désactivé s'il est actif (toujours démontable) ni pendant le
  // chargement (compteurs indéfinis).
  const boolFilterDisabled = (isActive: boolean, count?: number): boolean => !isActive && count !== undefined && nbHits !== undefined && (count === 0 || count === nbHits)

  const handiDisabled = boolFilterDisabled(params.handi === true, counts?.is_disabled_elligible)
  const urgentDisabled = futureStartDate || boolFilterDisabled(params.urgent === true, counts?.urgent)
  const smartApplyDisabled = boolFilterDisabled(params.smart_apply === true, counts?.smart_apply)

  // Type d'offres : 2 cases indépendantes portées par le param multi is_algo_company
  // (false = offres, true = entreprises à contacter). Les deux cochées : la sélection reste
  // affichée mais aucun filtre n'est envoyé à l'API (équivaut à « tout », cf. paramsToQuerystring).
  const offresChecked = params.is_algo_company?.includes(false) ?? false
  const entreprisesChecked = params.is_algo_company?.includes(true) ?? false
  const toggleOfferKind = (kind: "offres" | "entreprises") => {
    const target = kind === "entreprises"
    const current = params.is_algo_company ?? []
    const next = current.includes(target) ? current.filter((v) => v !== target) : [...current, target]
    navigate({ is_algo_company: next.length ? next : undefined })
  }
  const offerKindLabels = [...(offresChecked ? ["Offres d'emploi en alternance"] : []), ...(entreprisesChecked ? ["Entreprises à contacter"] : [])]

  const distanceActive = params.type_filter_label?.includes(TYPE_FILTER_LABEL_DISTANCE) ?? false
  // Compteur de la facette « Formation à distance » : valeur absente des facettes chargées = 0 résultat.
  const distanceCount = facets?.type_filter_label ? (facets.type_filter_label[TYPE_FILTER_LABEL_DISTANCE] ?? 0) : undefined
  const distanceDisabled = boolFilterDisabled(distanceActive, distanceCount)

  const toggleContract = (option: string) => {
    const current = params.contract_type ?? []
    const next = current.includes(option) ? current.filter((v) => v !== option) : [...current, option]
    navigate({ contract_type: next.length ? next : undefined })
  }

  const setStartDate = (value: string | undefined) => {
    // Date future : « Recrutement urgent » (dès que possible) n'a plus de sens → retiré.
    navigate({ start_date: value, urgent: isFutureDate(value) ? undefined : params.urgent })
  }

  const offerKindCheckboxOptions = [
    {
      label: "Offres d'emploi en alternance",
      nativeInputProps: { checked: offresChecked, onChange: () => toggleOfferKind("offres") },
    },
    {
      label: "Entreprises à contacter",
      hintText: "Vous pouvez leur adresser vos candidatures spontanées",
      nativeInputProps: { checked: entreprisesChecked, onChange: () => toggleOfferKind("entreprises") },
    },
  ]

  const contractCheckboxOptions = contractOptions.map((option) => ({
    label: option,
    nativeInputProps: { checked: params.contract_type?.includes(option) ?? false, onChange: () => toggleContract(option) },
  }))

  // La clé force un remount quand start_date est remis à zéro en externe (ex. « Réinitialiser les filtres »),
  // sinon React n'applique pas les mises à jour de defaultValue sur un champ non contrôlé après le montage.
  // Le filtre part au blur pour éviter une recherche avec une date partielle, champ vidé compris :
  // effacer un segment en cours de saisie rend la valeur vide, l'appliquer aussitôt remonterait le
  // champ (clé) et lui ferait perdre le focus. Entrée valide par ce même blur puis appelle `close`.
  // Désactivé avec le calendrier natif (cf. HIDDEN_DATE_PICKER_SX) : avec `closeOnPick`, une date
  // choisie dans le calendrier valide et ferme aussi (popper desktop : sur mobile, le sélecteur
  // émet des `change` en cours de défilement).
  // const startDateKeyEditRef = useRef(false)
  // const renderStartDateInput = (close?: () => void, closeOnPick = false) => (
  const renderStartDateInput = (close?: () => void) => (
    <Box sx={HIDDEN_DATE_PICKER_SX}>
      <Input
        key={params.start_date ?? ""}
        label="À partir du"
        hintText="Format attendu : JJ/MM/AAAA, par exemple 01/09/2026"
        nativeInputProps={{
          type: "date",
          defaultValue: params.start_date ?? "",
          // Sans changement, pas de navigation : elle marquerait le panneau « appliqué » (search_filter_opened).
          onBlur: (e) => {
            const value = e.target.value || undefined
            if (value !== params.start_date) setStartDate(value)
          },
          onKeyDown: (e) => {
            if (e.key === "Enter") {
              e.preventDefault()
              e.currentTarget.blur()
              close?.()
              return
            }
            // Désactivé avec le calendrier natif (cf. HIDDEN_DATE_PICKER_SX).
            // if (e.altKey || !DATE_EDIT_KEY.test(e.key)) return
            // // L'événement `input` d'une frappe est émis dans la même tâche que son keydown.
            // startDateKeyEditRef.current = true
            // setTimeout(() => {
            //   startDateKeyEditRef.current = false
            // })
          },
          // Désactivé avec le calendrier natif (cf. HIDDEN_DATE_PICKER_SX).
          // onChange: (e) => {
          //   if (e.target.value && closeOnPick && !startDateKeyEditRef.current) {
          //     e.currentTarget.blur()
          //     close?.()
          //   }
          // },
        }}
      />
    </Box>
  )

  if (variant === "sections") {
    return (
      <Box sx={{ "& > :last-child": { borderBottom: "none" } }}>
        {!isFormations && params.mode === "emplois" && (
          <MobileSection>
            <Checkbox small legend="Type d'offres d'emploi" options={offerKindCheckboxOptions} />
          </MobileSection>
        )}

        {!isFormations && <MobileSection title="Date de début de contrat">{renderStartDateInput(onClose)}</MobileSection>}

        <MobileSection>
          <RadioButtons
            small
            legend="Niveau d'études visé"
            options={[
              {
                label: "Indifférent",
                nativeInputProps: { checked: !selectedLevel, onChange: () => navigate({ level: undefined }) },
              },
              ...levelOptions.map((option) => ({
                label: option,
                nativeInputProps: { checked: option === selectedLevel, onChange: () => navigate({ level: [option] }) },
              })),
            ]}
          />
        </MobileSection>

        {!isFormations && (
          <MobileSection title={contractOptions.length > 0 ? undefined : "Type de contrat"}>
            {contractOptions.length > 0 ? (
              <Checkbox small legend="Type de contrat" options={contractCheckboxOptions} />
            ) : (
              <Box sx={{ fontSize: "0.875rem", color: fr.colors.decisions.text.disabled.grey.default }}>Aucune option disponible</Box>
            )}
          </MobileSection>
        )}

        {!isFormations && (
          <>
            <MobileSection>
              <Checkbox
                small
                options={[
                  {
                    label: withCount("Employeur handi-engagé", counts?.is_disabled_elligible),
                    nativeInputProps: { checked: params.handi === true, disabled: handiDisabled, onChange: () => navigate({ handi: params.handi ? undefined : true }) },
                  },
                ]}
              />
            </MobileSection>
            <MobileSection>
              <Checkbox
                small
                options={[
                  {
                    label: withCount("Recrutement urgent", counts?.urgent),
                    nativeInputProps: { checked: params.urgent === true, disabled: urgentDisabled, onChange: () => navigate({ urgent: params.urgent ? undefined : true }) },
                  },
                ]}
              />
            </MobileSection>
            <MobileSection>
              <Checkbox
                small
                options={[
                  {
                    label: withCount("Candidature simplifiée", counts?.smart_apply),
                    nativeInputProps: {
                      checked: params.smart_apply === true,
                      disabled: smartApplyDisabled,
                      onChange: () => navigate({ smart_apply: params.smart_apply ? undefined : true }),
                    },
                  },
                ]}
              />
            </MobileSection>
          </>
        )}

        {isFormations && (
          <MobileSection>
            <Checkbox
              small
              options={[
                {
                  label: "Formations à distance",
                  nativeInputProps: {
                    checked: distanceActive,
                    disabled: distanceDisabled,
                    onChange: () => navigate({ type_filter_label: distanceActive ? undefined : [TYPE_FILTER_LABEL_DISTANCE] }),
                  },
                },
              ]}
            />
          </MobileSection>
        )}
      </Box>
    )
  }

  return (
    <Box role="group" aria-label="Filtres" sx={{ display: "flex", flexWrap: "wrap", gap: fr.spacing("2v"), alignItems: "center" }}>
      {!isFormations && params.mode === "emplois" && (
        <SearchFilterChip
          label="Type d'offres d'emploi"
          activeLabel={offerKindLabels.length ? multiLabel(offerKindLabels) : undefined}
          active={offerKindLabels.length > 0}
          onOpenChange={trackDropdown("job_offer_type")}
          popperContent={
            <Box sx={CHECKBOX_POPPER_SX}>
              <Checkbox small legend="Type d'offres d'emploi" classes={{ legend: "fr-sr-only" }} options={offerKindCheckboxOptions} />
            </Box>
          }
        />
      )}

      {!isFormations && (
        <SearchFilterChip
          label="Date de début de contrat"
          activeLabel={params.start_date ? `À partir du ${formatDateFr(params.start_date)}` : undefined}
          active={Boolean(params.start_date)}
          onOpenChange={trackDropdown("contract_start_date")}
          popperContent={(close) => <Box sx={{ px: "16px", pt: "8px" }}>{renderStartDateInput(close /* , true : cf. HIDDEN_DATE_PICKER_SX */)}</Box>}
        />
      )}

      <SearchFilterChip
        label="Niveau d'études visé"
        activeLabel={selectedLevel}
        active={Boolean(selectedLevel)}
        disabled={levelOptions.length === 0}
        onOpenChange={trackDropdown("education_level")}
        popperContent={
          <Box>
            {levelOptions.map((option) => (
              <SearchChipOptionRow
                key={option}
                label={option}
                selected={option === selectedLevel}
                onSelect={() => navigate({ level: option === selectedLevel ? undefined : [option] })}
              />
            ))}
            {levelOptions.length === 0 && (
              <Box sx={{ px: "16px", py: "8px", fontSize: "0.875rem", color: fr.colors.decisions.text.disabled.grey.default }}>Aucune option disponible</Box>
            )}
          </Box>
        }
      />

      {!isFormations && (
        <SearchFilterChip
          label="Type de contrat"
          activeLabel={params.contract_type?.length ? multiLabel(params.contract_type) : undefined}
          active={Boolean(params.contract_type?.length)}
          disabled={contractOptions.length === 0}
          onOpenChange={trackDropdown("contract_type")}
          popperContent={
            <Box sx={CHECKBOX_POPPER_SX}>
              <Checkbox small legend="Type de contrat" classes={{ legend: "fr-sr-only" }} options={contractCheckboxOptions} />
            </Box>
          }
        />
      )}

      {!isFormations && (
        <>
          <SearchFilterChip
            label={withCount("Employeur handi-engagé", counts?.is_disabled_elligible)}
            active={params.handi === true}
            disabled={handiDisabled}
            onToggle={() => navigate({ handi: params.handi ? undefined : true })}
          />
          <SearchFilterChip
            label={withCount("Recrutement urgent", counts?.urgent)}
            active={params.urgent === true}
            disabled={urgentDisabled}
            onToggle={() => navigate({ urgent: params.urgent ? undefined : true })}
          />
          <SearchFilterChip
            label={withCount("Candidature simplifiée", counts?.smart_apply)}
            active={params.smart_apply === true}
            disabled={smartApplyDisabled}
            onToggle={() => navigate({ smart_apply: params.smart_apply ? undefined : true })}
          />
        </>
      )}

      {isFormations && (
        <SearchFilterChip
          label="Formations à distance"
          active={distanceActive}
          disabled={distanceDisabled}
          onToggle={() => navigate({ type_filter_label: distanceActive ? undefined : [TYPE_FILTER_LABEL_DISTANCE] })}
        />
      )}

      {hasActiveFilters(params) && (
        <Box
          component="button"
          type="button"
          className={fr.cx("fr-link", "fr-link--sm")}
          onClick={() => onNavigate(clearedFilters(params))}
          // Souligné en permanence (le soulignement DSFR par background-image ne s'applique qu'aux <a>).
          sx={{ ml: fr.spacing("2v"), textDecoration: "underline", textUnderlineOffset: "4px" }}
        >
          Réinitialiser les filtres
        </Box>
      )}
    </Box>
  )
}
